import React, { Component } from 'react';

import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormGroup from '@material-ui/core/FormGroup';
import Paper from '@material-ui/core/Paper';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import AnimatedNumber from 'react-animated-number';

import { IEventItem, IEventRegistration } from '../../../../store/player/events/store';

import Button from '../../../../components/Button';
import CardHeader from '../../../../components/CardHeader';
import CouponPicker from '../../../../components/CouponPicker';
import { formatTotal } from '../../../common/Utils/formatTotal';

interface IProps {
  item: IEventItem;
  onContinue: (checkout: ICheckoutState) => void;
  onCancel: () => void;
  onCouponCheck: (checkout: ICheckoutState, code: string) => Promise<any>;
}

interface ICoupon {
  codename: string;
  discountValue: string;
  title: string;
}

export interface ICheckoutState {
  coupon?: ICoupon;
  forbidden?: boolean;
  summary?: IEventRegistration;
  selectedAddons: {
    [key: string]: boolean;
  };
}

class EventCheckout extends Component<IProps, ICheckoutState> {
  private mounted: boolean;

  constructor(props: IProps) {
    super(props);
    this.mounted = false;

    this.state = {
      selectedAddons: {}
    };

    this.continue = this.continue.bind(this);
  }

  public async componentDidMount(): Promise<void> {
    this.mounted = true;

    const { eventRegistration: summary } = await this.props.onCouponCheck(
      { selectedAddons: {} },
      ''
    );

    if (!this.mounted) {
      return;
    }

    if (summary) {
      this.setState({ coupon: summary.coupon, summary });
    } else {
      this.setState({ forbidden: true });
    }
  }

  public componentWillUnmount(): void {
    this.mounted = false;
  }

  public continue(): void {
    this.props.onContinue(this.state);
  }

  public getTotal(): number {
    const { price } = this.props.item;
    const { summary } = this.state;

    return summary ? Number(summary.totalPrice) : Number(price.price);
  }

  public setCoupon = async (code: string) => {
    const { selectedAddons } = this.state;
    const { eventRegistration: summary } = await this.props.onCouponCheck({ selectedAddons }, code);

    this.setState({ coupon: summary.coupon, summary });

    return { coupon: summary.coupon };
  };

  public toggleAddon = (addonId: string) => async () => {
    const selectedAddons = {
      ...this.state.selectedAddons,
      [addonId]: !this.state.selectedAddons[addonId]
    };

    const code = this.state.summary ? (this.state.summary.coupon as any).codename : '';

    const { eventRegistration: summary } = await this.props.onCouponCheck({ selectedAddons }, code);

    this.setState({ selectedAddons, summary });
  };

  public render(): JSX.Element {
    const { item, onCancel } = this.props;

    const pendingMembership = this.state.summary ? this.state.summary.items.clubMembershipPrice : 0;
    const remainingSpots =
      item.totalPlayers - item.enrollments.filter(e => e.status === 'active').length;

    return (
      <div className="event-checkout wrapper">
        <Paper elevation={1} className="paper">
          <CardHeader label="Checkout" backTo={onCancel} />

          {this.state.forbidden ? (
            <div className="error-message">
              There is a problem with your enrollment, contact an admin to know more details.
            </div>
          ) : (
            <>
              {remainingSpots <= 0 && (
                <div className="disclaimer-view">
                  This event happens to be full. You will be in line for a spot should another
                  player withdraw. The waiting list is created in the order the registrations are
                  received. Fingers crossed you get in.
                </div>
              )}

              <div className="pricing-table">
                <div className="th">
                  <div>ENTRY FEES</div>
                </div>

                {Number(pendingMembership) > 0 && (
                  <div className="tr">
                    <div>Club Membership</div>

                    <div>${Number(pendingMembership).toFixed(2)}</div>
                  </div>
                )}

                <div className="tr">
                  <div>{item.price.name}</div>

                  <div>${Number(item.price.price).toFixed(2)}</div>
                </div>
              </div>

              {item.addons.length > 0 && (
                <div className="pricing-table">
                  <div className="th">
                    <div>EVENT EXTRAS</div>
                  </div>

                  {item.addons.map(addon => (
                    <FormGroup
                      row={true}
                      className="input checkbox"
                      key={`addon-wrapper-${addon.id}`}
                    >
                      <FormControlLabel
                        className="checkout-checkbox"
                        control={
                          <Checkbox
                            id={`addon-${addon.id}`}
                            onChange={this.toggleAddon(addon.id)}
                            icon={<CheckBoxOutlineBlankIcon color="primary" />}
                            checkedIcon={<CheckBoxIcon />}
                            value="on"
                            color="primary"
                          />
                        }
                        label={addon.name}
                      />

                      <div className="fee">${Number(addon.price).toFixed(2)}</div>
                    </FormGroup>
                  ))}
                </div>
              )}

              <div className="coupon-picker-container">
                <CouponPicker target="event" id={item.id} onChange={this.setCoupon} />
              </div>

              <div className="total">
                <div>Total</div>

                <AnimatedNumber
                  component="div"
                  stepPrecision={2}
                  value={this.getTotal()}
                  style={{
                    transition: '0.8s ease-out',
                    transitionProperty: 'background-color, color, opacity'
                  }}
                  duration={300}
                  formatValue={formatTotal}
                />
              </div>

              <Button
                id="checkout-continue"
                variant="contained"
                color="primary"
                className="button button-shadow"
                onClick={this.continue}
              >
                CONTINUE
              </Button>

              <Button
                variant="outlined"
                color="primary"
                className="button"
                onClick={onCancel}
                id="checkout-cancel"
              >
                DECLINE INVITATION
              </Button>
            </>
          )}
        </Paper>
      </div>
    );
  }
}

export { EventCheckout };
